import React, { useState } from "react";

type ContactMode = "Phone Call" | "WhatsApp" | "Email" | "Video Call";

type CounselorForm = {
  fullName: string;
  mobile: string;
  email: string;
  studentClass: string;
  courseInterest: string;
  contactMode: ContactMode;
  preferredTime: string;
  message: string;
};

const contactModes: ContactMode[] = [
  "Phone Call",
  "WhatsApp",
  "Email",
  "Video Call",
];

const counselorBenefits = [
  {
    title: "Course Guidance",
    desc: "Get help choosing the right course according to class, subject level and future goal.",
  },
  {
    title: "Exam Roadmap",
    desc: "Understand the correct preparation path for boards, foundation, JEE or NEET.",
  },
  {
    title: "Batch Selection",
    desc: "Find the best live batch, timing and learning plan according to your schedule.",
  },
  {
    title: "Parent Support",
    desc: "Parents can also connect with counselors for fee, course and progress-related guidance.",
  },
];

const counselorSteps = [
  {
    step: "01",
    title: "Fill Details",
    desc: "Student shares class, course interest and contact details.",
  },
  {
    step: "02",
    title: "Choose Time",
    desc: "Select preferred time and contact mode for counseling.",
  },
  {
    step: "03",
    title: "Counselor Connects",
    desc: "Our academic counselor contacts the student or parent.",
  },
  {
    step: "04",
    title: "Get Roadmap",
    desc: "Student gets course suggestion and learning plan.",
  },
];

const quickReasons = [
  "Need help choosing course",
  "Want JEE / NEET roadmap",
  "Live batch timing confusion",
  "Course fee details",
  "Parent counseling request",
  "Need help after registration",
];

const counselors = [
  {
    name: "Academic Counselor",
    role: "Course & Batch Guidance",
    timing: "09:00 AM - 07:00 PM",
    status: "Available",
  },
  {
    name: "Exam Guidance Team",
    role: "JEE, NEET & Board Roadmap",
    timing: "10:00 AM - 06:00 PM",
    status: "Available",
  },
  {
    name: "Admission Support",
    role: "Fee, Enrollment & Access Help",
    timing: "09:30 AM - 07:30 PM",
    status: "Available",
  },
];

const TalkToCounselor: React.FC = () => {
  const [form, setForm] = useState<CounselorForm>({
    fullName: "",
    mobile: "",
    email: "",
    studentClass: "",
    courseInterest: "",
    contactMode: "Phone Call",
    preferredTime: "",
    message: "",
  });
  

  const handleChange = (field: keyof CounselorForm, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

const [showSuccess, setShowSuccess] = useState(false);
const [isSubmitting, setIsSubmitting] = useState(false);

const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.fullName || !form.mobile || !form.studentClass || !form.courseInterest) {
      alert("Please fill name, mobile number, class and course interest.");
      return;
    }

    setIsSubmitting(true);

    try {
      // const response = await fetch("http://localhost:9090/api/enquiry/submit", {
      const response = await fetch("https://node-api.mediitacademy.com/api/enquiry/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      // 👉 YEH LINE MISSING THI: Backend ka response read karke 'data' mein store kar rahe hain
      const data = await response.json();

      if (response.ok && data.success) {
        // 1. Popup dikhayein
        setShowSuccess(true); 
        
        // 2. 4 second baad popup hatayein aur form reset karein
        setTimeout(() => {
          setShowSuccess(false);
          setForm({
            fullName: "",
            mobile: "",
            email: "",
            studentClass: "",
            courseInterest: "",
            contactMode: "Phone Call",
            preferredTime: "",
            message: "",
          });
        }, 4000);
      } else {
        alert("Server Error: " + (data.message || "Failed to submit request."));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Network Error: Could not connect to backend server.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToDashboard = () => {
    window.location.href = "/student-dashboard";
  };

  return (
    <div
      className="min-h-screen bg-[#f6f8fc] text-[#101827] font-['Inter']"
    >
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-white">
          <div className="absolute -left-24 top-0 h-96 w-96 rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute right-0 top-16 h-96 w-96 rounded-full bg-orange-100 blur-3xl" />
          <div className="absolute bottom-0 left-[45%] h-72 w-72 rounded-full bg-green-100 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-[#f8fbff] px-4 py-2 shadow-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                    C
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Talk To Counselor
                  </p>
                </div>

                <h1 className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[64px]">
                  Connect with an academic counselor for the right learning path.
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Confused about course, batch, live classes, JEE, NEET, board
                  preparation or fee details? Fill the form and our counselor
                  will contact you with proper guidance.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => {
                      const section = document.getElementById("counselor-form");
                      section?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white shadow-xl shadow-slate-900/15 transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Book Free Counseling
                  </button>

                  <button
                    onClick={goToDashboard}
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300"
                  >
                    Open Dashboard
                  </button>
                </div>

                <div className="mt-9 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-blue-600">Free</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Counseling Call
                    </p>
                  </div>

                  <div className="rounded-3xl border border-green-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-green-600">1:1</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Student Guidance
                    </p>
                  </div>

                  <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-orange-600">24h</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Response Time
                    </p>
                  </div>
                </div>
              </div>

              {/* RIGHT CARD */}
              <div className="lg:col-span-6">
                <div className="relative mx-auto max-w-[590px]">
                  <div className="absolute -left-4 top-14 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Calls Today
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-600">
                      48+
                    </p>
                  </div>

                  <div className="absolute -right-4 bottom-16 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Support
                    </p>
                    <p className="mt-1 text-2xl font-black text-green-600">
                      Active
                    </p>
                  </div>

                  <div className="rounded-[38px] border border-slate-100 bg-[#101827] p-5 shadow-[0_30px_90px_rgba(15,23,42,0.18)]">
                    <div className="rounded-[30px] bg-white p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-600">
                            Counseling Desk
                          </p>
                          <h2 className="mt-2 text-2xl font-black text-slate-950">
                            Get course advice before joining
                          </h2>
                          <p className="mt-2 text-sm leading-6 text-slate-500">
                            Our counselor will understand your class, goal and
                            preparation level before suggesting the best course.
                          </p>
                        </div>

                        <span className="rounded-full border border-green-100 bg-green-50 px-3 py-1 text-xs font-black text-green-600">
                          Online
                        </span>
                      </div>

                      <div className="mt-6 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-blue-50 p-4">
                          <p className="text-2xl font-black text-blue-600">
                            JEE
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Roadmap
                          </p>
                        </div>

                        <div className="rounded-2xl bg-green-50 p-4">
                          <p className="text-2xl font-black text-green-600">
                            NEET
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Guidance
                          </p>
                        </div>

                        <div className="rounded-2xl bg-orange-50 p-4">
                          <p className="text-2xl font-black text-orange-600">
                            Board
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Support
                          </p>
                        </div>

                        <div className="rounded-2xl bg-purple-50 p-4">
                          <p className="text-2xl font-black text-purple-600">
                            Batch
                          </p>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Selection
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 rounded-3xl bg-[#101827] p-5 text-white">
                        <p className="text-sm font-black">
                          What counselor will help with?
                        </p>

                        <div className="mt-4 space-y-3">
                          {[
                            "Best course according to class",
                            "Live batch and schedule guidance",
                            "Preparation roadmap for exams",
                            "Fee, access and enrollment support",
                          ].map((item) => (
                            <div key={item} className="flex items-center gap-3">
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-400/10 text-xs font-black text-green-300">
                                ✓
                              </span>
                              <p className="text-sm font-bold text-white/75">
                                {item}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          const section = document.getElementById("counselor-form");
                          section?.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="mt-5 w-full rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700"
                      >
                        Request Counselor Call
                      </button>
                    </div>
                  </div>

                  <div className="absolute -bottom-6 left-8 right-8 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-black text-slate-950">
                          Counseling Hours
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          Monday to Saturday · 09:00 AM to 07:00 PM
                        </p>
                      </div>
                      <span className="rounded-xl bg-green-50 px-4 py-2 text-xs font-black text-green-600">
                        Open
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="bg-[#f6f8fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-6">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Why Connect
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Counselor helps students choose the right direction.
                </h2>
              </div>

              <p className="text-sm leading-6 text-slate-500 lg:col-span-6">
                A counselor can guide students and parents about courses, batch
                timing, exam preparation plan, fee details and how to use the LMS
                properly after joining.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {counselorBenefits.map((item, index) => (
                <div
                  key={item.title}
                  className="group rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-lg font-black text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FORM SECTION */}
        <section id="counselor-form" className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              {/* LEFT INFO */}
              <div className="lg:col-span-5">
                <div className="sticky top-6 rounded-[34px] bg-[#101827] p-7 text-white shadow-2xl lg:p-8">
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-200">
                    Request Form
                  </p>

                  <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl">
                    Submit your details and get a callback.
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    Fill the counseling form carefully. Our counselor will contact
                    you according to your preferred time and contact mode.
                  </p>

                  <div className="mt-7 space-y-4">
                    {counselorSteps.map((item) => (
                      <div
                        key={item.step}
                        className="rounded-2xl border border-white/10 bg-white/10 p-4"
                      >
                        <div className="flex gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-sm font-black text-slate-950">
                            {item.step}
                          </div>

                          <div>
                            <h3 className="text-sm font-black text-white">
                              {item.title}
                            </h3>
                            <p className="mt-1 text-xs leading-5 text-white/60">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* FORM */}
             <div className="lg:col-span-7">
                <form
                  onSubmit={handleSubmit}
                  className="rounded-[34px] border border-slate-200 bg-[#f8fbff] p-5 shadow-sm lg:p-6"
                >
                  <div className="rounded-[28px] bg-white p-5 lg:p-6">
                    
                    {/* NAYA SUCCESS MESSAGE BOX */}
            {showSuccess && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 transition-all duration-500">
            <div className="w-full max-w-md animate-[scale-up_0.3s_ease-in-out] rounded-[32px] bg-white p-8 text-center shadow-2xl">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-green-50">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 text-3xl text-white shadow-lg shadow-green-500/30">
                  ✓
                </div>
              </div>
              <h3 className="text-2xl font-black tracking-tight text-slate-950">
                Request Submitted!
              </h3>
              <p className="mt-3 text-base leading-6 text-slate-500">
                Counseling request submitted successfully. Our expert counselor will contact you shortly to guide you.
              </p>
            </div>
          </div>
        )}
                    <div className="mb-6">
                      <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                        Student Details
                      </p>
                      <h2 className="mt-2 text-3xl font-black tracking-[-0.03em] text-slate-950">
                        Book free counseling session
                      </h2>
                      <p className="mt-2 text-sm leading-6 text-slate-500">
                        Our team will contact you for course guidance and
                        learning plan discussion.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Full Name
                        </label>
                        <input
                          value={form.fullName}
                          onChange={(e) =>
                            handleChange("fullName", e.target.value)
                          }
                          placeholder="Enter student name"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Mobile Number
                        </label>
                        <input
                          value={form.mobile}
                          onChange={(e) => handleChange("mobile", e.target.value)}
                          placeholder="Enter mobile number"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Email Address
                        </label>
                        <input
                          value={form.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="Enter email address"
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Student Class
                        </label>
                        <select
                          value={form.studentClass}
                          onChange={(e) =>
                            handleChange("studentClass", e.target.value)
                          }
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                        >
                          <option value="">Select class</option>
                          <option>Class 7</option>
                          <option>Class 8</option>
                          <option>Class 9</option>
                          <option>Class 10</option>
                          <option>Class 11</option>
                          <option>Class 12</option>
                          <option>After 12</option>
                          <option>Parent Inquiry</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Course Interest
                        </label>
                        <select
                          value={form.courseInterest}
                          onChange={(e) =>
                            handleChange("courseInterest", e.target.value)
                          }
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                        >
                          <option value="">Select course</option>
                          <option>Foundation Course</option>
                          <option>Board Preparation</option>
                          <option>JEE Preparation</option>
                          <option>NEET Preparation</option>
                          <option>Test Series</option>
                          <option>Study Notes</option>
                          <option>Not Sure Yet</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                          Preferred Time
                        </label>
                        <select
                          value={form.preferredTime}
                          onChange={(e) =>
                            handleChange("preferredTime", e.target.value)
                          }
                          className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                        >
                          <option value="">Select time slot</option>
                          <option>09:00 AM - 11:00 AM</option>
                          <option>11:00 AM - 01:00 PM</option>
                          <option>01:00 PM - 03:00 PM</option>
                          <option>03:00 PM - 05:00 PM</option>
                          <option>05:00 PM - 07:00 PM</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-5">
                      <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                        Preferred Contact Mode
                      </label>

                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {contactModes.map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => handleChange("contactMode", mode)}
                            className={`rounded-2xl border px-4 py-3 text-sm font-black transition-all duration-300 ${
                              form.contactMode === mode
                                ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/10"
                                : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-600"
                            }`}
                          >
                            {mode}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-5">
                      <label className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                        Message / Question
                      </label>
                      <textarea
                        value={form.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        placeholder="Write your question or confusion here..."
                        rows={5}
                        className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm font-semibold outline-none transition-all focus:border-blue-300"
                      />
                    </div>

                    <div className="mt-5 rounded-[24px] bg-blue-50 p-5">
                      <p className="text-sm font-black text-slate-950">
                        Quick reasons for counseling
                      </p>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {quickReasons.map((reason) => (
                          <button
                            key={reason}
                            type="button"
                            onClick={() => {
                              const nextMessage = form.message
                                ? `${form.message}\n${reason}`
                                : reason;

                              handleChange("message", nextMessage);
                            }}
                            className="rounded-full bg-white px-4 py-2 text-xs font-black text-blue-600 transition-all hover:bg-blue-600 hover:text-white"
                          >
                            {reason}
                          </button>
                        ))}
                      </div>
                    </div>

                   <button
                      type="submit"
                      disabled={isSubmitting} // Submit hote time disable kar dein
                      className={`mt-6 w-full rounded-2xl px-5 py-4 text-sm font-black text-white shadow-xl transition-all duration-300 ${
                        isSubmitting 
                          ? "bg-blue-400 cursor-not-allowed" // Loading state color
                          : "bg-blue-600 hover:-translate-y-1 hover:bg-blue-700 shadow-blue-600/20"
                      }`}
                    >
                      {isSubmitting ? "Submitting Request..." : "Submit Counseling Request"}
                    </button>

                    <p className="mt-3 text-center text-xs font-semibold text-slate-500">
                      This form is frontend-ready. You can connect it with Spring
                      Boot or Node.js backend later.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* COUNSELOR TYPES */}
        <section className="bg-[#f6f8fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Available Teams
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Connect with the right support team.
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Based on the student need, counseling request can be assigned to
                academic counselor, exam guidance team or admission support.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {counselors.map((item) => (
                <div
                  key={item.name}
                  className="group rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-xl"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-xl font-black text-blue-600 transition-all group-hover:bg-blue-600 group-hover:text-white">
                      {item.name.charAt(0)}
                    </div>

                    <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-black text-green-600">
                      {item.status}
                    </span>
                  </div>

                  <h3 className="mt-5 text-2xl font-black tracking-[-0.03em] text-slate-950">
                    {item.name}
                  </h3>

                  <p className="mt-2 text-sm font-bold text-slate-500">
                    {item.role}
                  </p>

                  <div className="mt-5 rounded-2xl bg-[#f8fbff] p-4">
                    <p className="text-xs font-bold text-slate-500">
                      Available Timing
                    </p>
                    <p className="mt-1 text-sm font-black text-slate-950">
                      {item.timing}
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      const section = document.getElementById("counselor-form");
                      section?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-6 w-full rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Connect Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    Need Academic Guidance?
                  </p>

                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Let the counselor help you choose the right course.
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Whether you are preparing for boards, foundation, JEE or
                    NEET, our counselor can help you understand the right path,
                    batch and learning plan.
                  </p>
                </div>

                <button
                  onClick={() => {
                    const section = document.getElementById("counselor-form");
                    section?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                >
                  Book Counseling
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TalkToCounselor;